import { useEffect, useRef } from 'react'
import { Moon, Calendar, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import {
  getCalendarDays,
  isSameDay,
  isToday,
  isPastDate,
  type SnoozeOption,
} from '@/lib/snoozeTimeUtils'
import type { SnoozePickerMode } from '@/hooks/useSnoozePicker'

interface SnoozePickerProps {
  isOpen: boolean
  mode: SnoozePickerMode
  presetOptions: SnoozeOption[]
  selectedPresetIndex: number
  selectedDate: Date
  selectedTimeIndex: number
  currentMonth: Date
  timeSlots: string[]
  onSelectPreset: (index: number) => void
  onSelectDate: (date: Date) => void
  onSelectTime: (index: number) => void
  onNavigateMonth: (direction: -1 | 1) => void
  onSetMode: (mode: SnoozePickerMode) => void
  onConfirmPreset: () => void
  onConfirmCustom: () => void
  onClose: () => void
  isLoading: boolean
}

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]

const DAY_NAMES = ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sá']

export default function SnoozePicker({
  isOpen,
  mode,
  presetOptions,
  selectedPresetIndex,
  selectedDate,
  selectedTimeIndex,
  currentMonth,
  timeSlots,
  onSelectPreset,
  onSelectDate,
  onSelectTime,
  onNavigateMonth,
  onSetMode,
  onConfirmPreset,
  onConfirmCustom,
  onClose,
  isLoading,
}: SnoozePickerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const timeListRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    // Delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Scroll selected time into view in custom mode
  useEffect(() => {
    if (mode === 'custom' && timeListRef.current) {
      const selectedEl = timeListRef.current.querySelector('[data-selected="true"]')
      selectedEl?.scrollIntoView({ block: 'center' })
    }
  }, [mode, selectedTimeIndex])

  if (!isOpen) return null

  const calendarDays = getCalendarDays(
    currentMonth.getFullYear(),
    currentMonth.getMonth()
  )

  return (
    <div
      ref={containerRef}
      className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden"
      style={{ minWidth: mode === 'presets' ? '320px' : '480px' }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 text-gray-600">
        <Moon className="w-4 h-4" />
        <span className="text-sm">Adiar até</span>
      </div>

      {mode === 'presets' ? (
        /* Preset Options */
        <div className="py-1">
          {presetOptions.map((option, index) => (
            <button
              key={option.id}
              onClick={() => {
                onSelectPreset(index)
                onConfirmPreset()
              }}
              onMouseEnter={() => onSelectPreset(index)}
              disabled={isLoading}
              className={`w-full text-left px-4 py-2.5 flex items-center justify-between transition-colors cursor-pointer ${
                index === selectedPresetIndex
                  ? 'bg-gray-100'
                  : 'hover:bg-gray-50'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span className="text-sm text-gray-900">{option.label}</span>
              <span className="text-sm text-gray-500">{option.subLabel}</span>
            </button>
          ))}

          {/* Custom option */}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              onClick={() => onSetMode('custom')}
              onMouseEnter={() => onSelectPreset(presetOptions.length)}
              disabled={isLoading}
              className={`w-full text-left px-4 py-2.5 flex items-center gap-2 transition-colors cursor-pointer ${
                selectedPresetIndex === presetOptions.length
                  ? 'bg-gray-100'
                  : 'hover:bg-gray-50'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-900">Personalizado...</span>
            </button>
          </div>
        </div>
      ) : (
        /* Custom Calendar + Time Picker */
        <div className="flex">
          {/* Calendar */}
          <div className="p-4 border-r border-gray-100">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => onNavigateMonth(-1)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-sm font-medium text-gray-900">
                {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <button
                onClick={() => onNavigateMonth(1)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAY_NAMES.map((day) => (
                <div
                  key={day}
                  className="w-8 h-8 flex items-center justify-center text-xs font-medium text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map(({ date, isCurrentMonth }, index) => {
                const isSelected = isSameDay(date, selectedDate)
                const isTodayDate = isToday(date)
                const isPast = isPastDate(date) && !isTodayDate

                return (
                  <button
                    key={index}
                    onClick={() => !isPast && onSelectDate(date)}
                    disabled={isPast}
                    className={`w-8 h-8 flex items-center justify-center text-sm rounded-full transition-colors ${
                      isSelected
                        ? 'bg-orange-500 text-white font-medium'
                        : isTodayDate
                        ? 'bg-gray-100 font-medium text-gray-900'
                        : isCurrentMonth
                        ? isPast
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-100'
                        : 'text-gray-400'
                    }`}
                  >
                    {date.getDate()}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Time slots */}
          <div className="w-32 flex flex-col">
            <div
              ref={timeListRef}
              className="flex-1 overflow-y-auto max-h-72"
            >
              {timeSlots.map((time, index) => (
                <button
                  key={time}
                  data-selected={index === selectedTimeIndex}
                  onClick={() => onSelectTime(index)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 cursor-pointer ${
                    index === selectedTimeIndex
                      ? 'bg-orange-50 text-orange-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Moon className="w-3.5 h-3.5" />
                  {time}
                </button>
              ))}
            </div>

            {/* Confirm button */}
            <div className="p-3 border-t border-gray-100">
              <button
                onClick={onConfirmCustom}
                disabled={isLoading}
                className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adiando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard hints */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-600">↑</kbd>
          <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-600">↓</kbd>
          <span>navegar</span>
        </span>
        <span className="flex items-center gap-1">
          <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-600">↵</kbd>
          <span>selecionar</span>
        </span>
        <span className="flex items-center gap-1">
          <kbd className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-gray-600">Esc</kbd>
          <span>fechar</span>
        </span>
      </div>
    </div>
  )
}
